import { useMemo, useState, type ComponentType } from "react";
import {
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  CircleDollarSign,
  FileText,
  Fuel,
  HardHat,
  Info,
  Landmark,
  MapPin,
  Package,
  Percent,
  PieChart,
  Users,
  Wrench,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Loader } from "@/components/ui/Loader";
import { formatCurrency, formatDateTime, formatNumber } from "@/utils/format";
import { ContaStat } from "../chartUtils";
import { ModalEntityTitle, OutcomeTable, SummaryBanner } from "./FinanceiroModalContent";

/** Paleta alinhada à referência visual do card. */
const PALETTE = [
  "#9F1239",
  "#DB2777",
  "#C084FC",
  "#EA580C",
  "#F59E0B",
  "#F87171",
  "#2563EB",
  "#7C3AED",
  "#3B82F6",
  "#10B981",
  "#14B8A6",
  "#64748B",
];

type LucideIcon = ComponentType<{ className?: string }>;

const FALLBACK_ICONS: LucideIcon[] = [
  Package, MapPin, Users, Briefcase, Percent, Fuel, Landmark, FileText, Wrench, HardHat, Building2, CircleDollarSign,
];

function iconForPlan(name: string, index: number): LucideIcon {
  const n = name.toLowerCase();
  if (/insumo|material|obra\b/.test(n) && !/m[aã]o de obra|servi[cç]o/.test(n)) return HardHat;
  if (/terreno|im[oó]vel|lote/.test(n)) return MapPin;
  if (/m[aã]o de obra|pedreiro|gesseiro|auxiliar|eletric|encanad/.test(n)) return Users;
  if (/servi[cç]o|terceiriz/.test(n)) return Briefcase;
  if (/comiss[aã]o|venda/.test(n)) return Percent;
  if (/combust|diesel|gasolina/.test(n)) return Fuel;
  if (/empr[eé]stimo|financiamento|banco/.test(n)) return Landmark;
  if (/cart[oó]rio|averba|registro/.test(n)) return FileText;
  if (/manuten|reparo|ferrament/.test(n)) return Wrench;
  if (/aluguel|imobili/.test(n)) return Building2;
  return FALLBACK_ICONS[index % FALLBACK_ICONS.length];
}

function hexToRgba(hex: string, alpha: number): string {
  const raw = hex.replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildPctAxis(maxPct: number): { axisMax: number; ticks: number[] } {
  if (maxPct <= 0) return { axisMax: 40, ticks: [0, 10, 20, 30, 40] };
  const padded = maxPct * 1.08;
  const axisMax = Math.max(10, Math.ceil(padded / 10) * 10);
  const step = axisMax / 4;
  return { axisMax, ticks: [0, step, step * 2, step * 3, step * 4] };
}

interface Props {
  data: ContaStat[];
  loading: boolean;
}

export function DespesasPorConta({ data, loading }: Props) {
  const [selected, setSelected] = useState<ContaStat | null>(null);

  const total = useMemo(() => data.reduce((s, d) => s + d.valor, 0), [data]);
  const { axisMax, ticks } = useMemo(() => {
    const maxPct = data.reduce((m, d) => Math.max(m, d.pct ?? 0), 0);
    return buildPctAxis(maxPct);
  }, [data]);

  const updatedAt = useMemo(() => formatDateTime(new Date().toISOString()), [data]);

  const empty = !loading && data.length === 0;

  return (
    <>
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-card">
        {/* Cabeçalho */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold leading-tight text-slate-800">
              Despesas por Plano Financeiro
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Planos por valor total no período · % sobre o total de despesas
            </p>
          </div>

          <div className="flex items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50 px-3.5 py-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
              <PieChart className="h-4 w-4" aria-hidden />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Total de despesas
              </p>
              <p className="text-sm font-bold tabular-nums text-slate-900">
                {loading ? "—" : formatCurrency(total, true)}
              </p>
            </div>
          </div>
        </div>

        {/* Corpo */}
        {loading ? (
          <div className="flex min-h-[220px] items-center justify-center">
            <Loader size="md" label="Carregando dados..." />
          </div>
        ) : empty ? (
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 text-slate-400">
            <BookOpen className="h-10 w-10 opacity-40" />
            <p className="text-sm font-medium">Nenhum plano financeiro identificado no período.</p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-col gap-1">
            {/* Eixo % no topo */}
            <div className="flex min-w-0 gap-2 sm:gap-3">
              <div className="w-[148px] shrink-0 sm:w-[168px]" aria-hidden />
              <div className="relative min-h-5 min-w-0 flex-1">
                <div className="pointer-events-none absolute inset-0 text-[10px] tabular-nums text-slate-400 sm:text-[11px]">
                  {ticks.map((t) => (
                    <span
                      key={t}
                      className="absolute top-0 -translate-x-1/2"
                      style={{ left: `${axisMax > 0 ? (t / axisMax) * 100 : 0}%` }}
                    >
                      {formatNumber(t, 0)}%
                    </span>
                  ))}
                </div>
              </div>
              <div className="w-[118px] shrink-0 sm:w-[132px]" aria-hidden />
            </div>

            <div className="min-h-0 max-h-[280px] overflow-y-auto overscroll-y-contain [scrollbar-gutter:stable] pr-0.5 sm:max-h-[320px]">
              <div className="flex flex-col gap-1.5 py-1">
                {data.map((row, idx) => {
                  const color = PALETTE[idx % PALETTE.length];
                  const pct = row.pct ?? 0;
                  const barWidth = axisMax > 0 ? Math.min(100, (pct / axisMax) * 100) : 0;
                  const Icon = iconForPlan(row.name, idx);

                  return (
                    <button
                      key={`${row.accountId ?? row.name}-${idx}`}
                      type="button"
                      onClick={() => setSelected(row)}
                      className="group flex w-full min-w-0 items-center gap-2 rounded-lg py-1 pl-0.5 pr-0.5 text-left transition-colors hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 sm:gap-3"
                    >
                      <div className="flex w-[148px] shrink-0 items-center gap-2 sm:w-[168px]">
                        <span
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white shadow-sm"
                          style={{ backgroundColor: color }}
                          aria-hidden
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <span
                          className="min-w-0 truncate text-xs font-medium text-slate-700 group-hover:text-slate-900 sm:text-[13px]"
                          title={row.name}
                        >
                          {row.name}
                        </span>
                      </div>

                      <div className="relative h-7 min-w-0 flex-1">
                        <div className="pointer-events-none absolute inset-0" aria-hidden>
                          {ticks.map((t) => (
                            <div
                              key={t}
                              className="absolute inset-y-0 border-l border-dashed border-slate-200/90"
                              style={{ left: `${axisMax > 0 ? (t / axisMax) * 100 : 0}%` }}
                            />
                          ))}
                        </div>
                        <div className="relative z-[1] flex h-full items-center">
                          <div
                            className="h-[18px] rounded-r-full shadow-sm transition-[width] duration-500"
                            style={{
                              width: `${barWidth}%`,
                              backgroundColor: color,
                              minWidth: barWidth > 0 ? "6px" : undefined,
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex w-[118px] shrink-0 items-center justify-end gap-1.5 sm:w-[132px]">
                        <span
                          className="text-xs font-bold tabular-nums sm:text-[13px]"
                          style={{ color }}
                        >
                          {formatCurrency(row.valor, true)}
                        </span>
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums sm:text-[11px]"
                          style={{
                            color,
                            backgroundColor: hexToRgba(color, 0.14),
                          }}
                        >
                          {formatNumber(pct, 1)}%
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Rodapé */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-slate-100 pt-3 text-[11px] text-slate-400">
          <p className="inline-flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Dados referentes ao período selecionado.
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Fonte: Sistema de Gestão</span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Atualizado em: {updatedAt}
            </span>
          </div>
        </div>
      </div>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="xl"
        title={selected && (
          <ModalEntityTitle
            icon={<BookOpen className="w-4 h-4" />}
            label={selected.name}
            iconBg="bg-indigo-50"
            iconColor="text-indigo-600"
          />
        )}
      >
        {selected && (
          <>
            <SummaryBanner
              label="Total da Conta"
              value={formatCurrency(selected.valor)}
              color="#6366f1"
              subtitle={`${selected.quantidade} parcela${selected.quantidade !== 1 ? "s" : ""}${selected.pct != null ? ` · ${formatNumber(selected.pct, 1)}% do total` : ""}${selected.accountId && selected.accountId !== selected.name ? ` · ID ${selected.accountId}` : ""}`}
            />
            <OutcomeTable items={selected.items} />
          </>
        )}
      </Modal>
    </>
  );
}
